import {
  CONTACT_TYPE_OPTIONS,
  getAllowedPlayResults,
  isContactTypeAllowed,
  PITCH_RESULT_OPTIONS,
  PITCH_TYPE_OPTIONS,
  PLAY_RESULT_OPTIONS,
  TagOption,
} from "../../constants/tagOptions";
import { Activity, HitData, Sport } from "../../types/gameFull";
import {
  ALL_EDITED,
  getAutoResults,
  NOT_EDITED,
  ResultsEdited,
  SituationBefore,
} from "../../utils/autoResults";
import { getContactTypeFor, PitchTags } from "../../utils/pitchTagging";

/** The Pitch Details form's sections, in tagging order. */
export const SECTIONS = [
  "pitchType",
  "pitchResult",
  "contactType",
  "playResult",
  "results",
] as const;

export type Section = (typeof SECTIONS)[number];

// Before the pitch arrives only the pitch type can be marked
export const WAITING_SECTIONS: readonly Section[] = ["pitchType"];

/** Whether the scorekeeper can open or edit the section right now. */
export const isSectionAvailable = (
  section: Section,
  tags: PitchTags,
  isWaiting: boolean,
) =>
  isWaiting
    ? WAITING_SECTIONS.includes(section)
    : isSectionEnabled(section, tags);

/** Sections picked from a list of options (all but Results) */
export type TagKey = "pitchType" | "pitchResult" | "playResult" | "contactType";

const getSectionOptions = (sport: Sport): Record<TagKey, TagOption[]> => ({
  pitchType: PITCH_TYPE_OPTIONS[sport],
  pitchResult: PITCH_RESULT_OPTIONS,
  playResult: PLAY_RESULT_OPTIONS,
  contactType: CONTACT_TYPE_OPTIONS,
});

// Built once per sport, so each option list keeps its identity across renders
export const SECTION_OPTIONS: Record<Sport, Record<TagKey, TagOption[]>> = {
  baseball: getSectionOptions("baseball"),
  softball: getSectionOptions("softball"),
};

export const isSectionEnabled = (section: Section, tags: PitchTags) => {
  if (section === "playResult") {
    return getAllowedPlayResults(tags.pitchResult).length > 0;
  }
  if (section === "contactType") {
    return isContactTypeAllowed(tags.pitchResult);
  }
  return true;
};

/**
 * Next enabled section after `from` (the first when nothing is open),
 * stopping at the last section.
 */
export const getNextSection = (
  from: Section | null,
  tags: PitchTags,
  sections: readonly Section[] = SECTIONS,
): Section => {
  const start = from ? sections.indexOf(from) : -1;
  return (
    sections.slice(start + 1).find((s) => isSectionEnabled(s, tags)) ??
    sections[sections.length - 1]
  );
};

export const getPreviousSection = (
  from: Section | null,
  tags: PitchTags,
  sections: readonly Section[] = SECTIONS,
): Section => {
  const start = from ? sections.indexOf(from) : sections.length;
  return (
    sections
      .slice(0, start)
      .reverse()
      .find((s) => isSectionEnabled(s, tags)) ?? sections[0]
  );
};

/** Clears play result / contact type that the pitch result no longer allows. */
export const withGating = (tags: PitchTags): PitchTags => ({
  ...tags,
  playResult:
    tags.playResult &&
    getAllowedPlayResults(tags.pitchResult).includes(tags.playResult)
      ? tags.playResult
      : null,
  contactType: isContactTypeAllowed(tags.pitchResult) ? tags.contactType : null,
});

/**
 * Fills each tag the scorekeeper hasn't confirmed from the tracker's current
 * suggestions and the rules they imply, keeping a value when there's nothing
 * new to replace it with.
 */
export const withSuggestions = (
  tags: PitchTags,
  confirmed: ReadonlySet<TagKey>,
  suggested: { pitchType: string | null; pitchResult: string | null },
  hitData: HitData | null,
  getAutoPlayResult: (pitchResult: string | null) => string | null,
): PitchTags => {
  const isKept = (key: TagKey) => confirmed.has(key);
  const pitchResult = isKept("pitchResult")
    ? tags.pitchResult
    : (suggested.pitchResult ?? tags.pitchResult);
  return withGating({
    ...tags,
    pitchType: isKept("pitchType")
      ? tags.pitchType
      : (suggested.pitchType ?? tags.pitchType),
    pitchResult,
    contactType: isKept("contactType")
      ? tags.contactType
      : (getContactTypeFor(pitchResult, hitData) ?? tags.contactType),
    playResult: isKept("playResult")
      ? tags.playResult
      : (getAutoPlayResult(pitchResult) ?? tags.playResult),
  });
};

/** Fills Results From This Play from the play result and prior situation. */
export const withAutoResults = (
  tags: PitchTags,
  before: SituationBefore,
): PitchTags => ({
  ...tags,
  ...getAutoResults(tags.pitchResult, tags.playResult, before),
});

export const TAG_KEYS: readonly TagKey[] = [
  "pitchType",
  "pitchResult",
  "contactType",
  "playResult",
];

/** Whether the section waits on the scorekeeper, so it opens. */
export const needsPick = (section: Section, tags: PitchTags) => {
  if (section === "results" || !isSectionEnabled(section, tags)) return false;
  if (tags[section] !== null) return false;
  // A ball, strike or foul only has an outcome when it ends the at bat, and
  // ball four / strike three are filled in. Blank is the usual answer, so
  // only a ball in play waits on one
  if (section === "playResult") return tags.pitchResult === "InPlay";
  return true;
};

/**
 * The section to open: the first that waits on a pick. Otherwise Results
 * (always shown) is active, or nothing while waiting on the pitch.
 */
export const getSectionToOpen = (
  tags: PitchTags,
  isWaiting = false,
): Section | null => {
  // The classifier's pitch type comes with the pitch and the rest depends on
  // it, so the waiting row asks for nothing
  if (isWaiting) return null;
  return SECTIONS.find((section) => needsPick(section, tags)) ?? "results";
};

// A saved pitch opens collapsed; an untagged one at its first missing pick
export const getInitialSection = (
  tags: PitchTags,
  activity: Activity | null,
): Section | null =>
  activity?.result ? "results" : getSectionToOpen(tags, !activity);

/**
 * Tags the scorekeeper picked (or saved), as opposed to filled in by the
 * tracker or a rule: those show "Auto Detected".
 */
export const getInitialConfirmed = (
  activity: Activity | null,
): ReadonlySet<TagKey> => new Set(activity?.result ? TAG_KEYS : []);

/**
 * Confirms `picked`, and un-confirms any other tag the change filled in or
 * cleared: it's the form's value now, not the scorekeeper's.
 */
export const updateConfirmed = (
  confirmed: ReadonlySet<TagKey>,
  before: PitchTags,
  after: PitchTags,
  picked?: TagKey,
): ReadonlySet<TagKey> => {
  const next = new Set(confirmed);
  for (const key of TAG_KEYS) {
    if (key !== picked && before[key] !== after[key]) next.delete(key);
  }
  if (picked) next.add(picked);
  return next;
};

// Saved results are the scorekeeper's; only a new play result replaces them
export const getInitialEdited = (activity: Activity | null): ResultsEdited =>
  activity?.result ? ALL_EDITED : NOT_EDITED;
